import React from 'react';
import { Target, Award, Zap, Search, BarChart } from 'lucide-react';

const benefits = [
  {
    icon: Target,
    title: 'Expanded Reach & Visibility',
    description:
      'Distribute your content across multiple high-authority platforms, industry publications, and social media channels. Increase brand visibility and expose your business to a larger, more relevant B2B audience.',
    featured: true,
  },
  {
    icon: Award,
    title: 'Strengthened Brand Authority',
    description:
      'Establish your brand as an industry leader by distributing content through reputable channels. Build credibility by syndicating high-quality content across respected B2B platforms.',
    featured: false,
  },
  {
    icon: Zap,
    title: 'Enhanced Lead Generation',
    description:
      'Generate high-quality leads through strategically syndicated content on platforms that attract your target audience. Our approach ensures your content reaches prospects actively searching for solutions.',
    featured: false,
  },
  {
    icon: Search,
    title: 'SEO & Domain Authority',
    description:
      'Boost your search engine rankings and domain authority through quality backlinks from reputable syndication partners. Improve your online visibility and organic search performance.',
    featured: false,
  },
  {
    icon: BarChart,
    title: 'Measurable ROI & Analytics',
    description:
      'Track every campaign with comprehensive analytics dashboards. Measure reach, engagement, lead quality, and conversion rates with full transparency on your content syndication investment.',
    featured: false,
  },
];

export default function BenefitsSection() {
  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="py-20 md:py-32 bg-slate-50"
      aria-label="Benefits of B2B Content Syndication"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block text-sm font-semibold tracking-wide uppercase text-[#4AC5DB] mb-4">
            Benefits
          </span>
          <h2
            data-testid="benefits-heading"
            className="font-['Outfit'] text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight"
          >
            Benefits of Our B2B Content Syndication Services
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            Partner with leading content syndication vendors to unlock powerful growth opportunities
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-fr">
          {/* Featured card - spans 2 rows on desktop */}
          <div
            data-testid={`benefit-card-0`}
            className="group relative overflow-hidden rounded-2xl bg-white p-8 md:p-10 border border-slate-100 hover:border-[#4AC5DB]/30 transition-all duration-300 hover:shadow-lg md:row-span-2"
          >
            <div className="w-14 h-14 rounded-xl bg-[#4AC5DB]/10 flex items-center justify-center mb-6 group-hover:bg-[#4AC5DB]/20 transition-colors">
              <Target className="w-7 h-7 text-[#4AC5DB]" strokeWidth={1.5} />
            </div>
            <h3 className="font-['Outfit'] text-2xl md:text-3xl font-medium text-slate-900 mb-4">
              {benefits[0].title}
            </h3>
            <p className="text-base text-slate-600 leading-relaxed">
              {benefits[0].description}
            </p>
            <div className="mt-8 pt-6 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Key Outcomes</p>
              <ul className="space-y-2">
                {['Wider brand exposure', 'Targeted B2B audience', 'Multi-channel distribution'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#4AC5DB]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Remaining cards */}
          {benefits.slice(1).map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.title}
                data-testid={`benefit-card-${idx + 1}`}
                className="group relative overflow-hidden rounded-2xl bg-white p-8 border border-slate-100 hover:border-[#4AC5DB]/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-12 h-12 rounded-xl bg-[#4AC5DB]/10 flex items-center justify-center mb-5 group-hover:bg-[#4AC5DB]/20 transition-colors">
                  <Icon className="w-6 h-6 text-[#4AC5DB]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
